import React, { useState, useEffect } from 'react';
import { SettingsPage } from '../../components/SettingsPage/SettingsPage';
import { RadioSetting } from '../../components/SettingCards/RadioSetting/RadioSetting';
import {
	KEYBOARD_SETTING_IDS,
	KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS,
} from '../../config/SettingIds';
import { getSettingConfig, RadioSettingConfig } from '../../config/SettingsDefinitions';
import { SettingsNativeBridgeFactory } from '../../bridge/SettingsNativeBridgeFactory';
import { SettingsLogger, logConstants, AotCategory } from '../../utils/SettingsLogger';
import { KeyboardUnicodeModeSetting } from '../../customSettings/KeyboardUnicodeModeSetting/KeyboardUnicodeModeSetting';

interface SettingState {
	value: any;
	disabled: boolean;
}

interface KeyboardInputModeSettingsPageProps {
	title: string;
	onNavigatingBack: () => void;
}

export const KeyboardInputModeSettingsPage: React.FC<
	KeyboardInputModeSettingsPageProps
> = ({ title, onNavigatingBack }) => {
	const [inputMode, setInputMode] = useState<string>('automatic');
	const [unicodeModeSettings, setUnicodeModeSettings] = useState<
		Record<string, SettingState>
	>({});
	const [isLoading, setIsLoading] = useState(true);

	// Load current setting values
	useEffect(() => {
		const loadSettings = async () => {
			try {
				const bridge = SettingsNativeBridgeFactory.getBridge();

				// Load input mode selection
				const inputModeResponse = await bridge.getSetting({
					settingKey: KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE_SELECTION,
				});

				if (
					inputModeResponse &&
					inputModeResponse.status === 0 &&
					inputModeResponse.data?.settingValue
				) {
					setInputMode(inputModeResponse.data.settingValue);
				}

				// Load Unicode mode settings
				const unicodeModeSettingIds = [
					KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.CTRL_CHARACTER,
					KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.ALT_CHARACTER,
					KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.WINDOWS_LOGO_KEY,
					KEYBOARD_INPUT_UNICODE_MODE_SETTING_IDS.FKEYS_WITH_OPTION,
				];

				const unicodeModeResponse = await bridge.getSettings({
					settings: unicodeModeSettingIds.map((settingKey) => ({ settingKey })),
				});

				if (unicodeModeResponse && unicodeModeResponse.status === 0) {
					const newUnicodeState: Record<string, SettingState> = {};
					unicodeModeResponse.data.settings?.forEach((setting: any) => {
						let value = setting.settingValue ?? false;
						if (value === 'true') value = true;
						if (value === 'false') value = false;
						newUnicodeState[setting.settingKey] = {
							value,
							disabled: setting.enabled !== undefined ? !setting.enabled : false,
						};
					});
					setUnicodeModeSettings(newUnicodeState);
				}

				setIsLoading(false);
			} catch (error) {
				SettingsLogger.getInstance().write(
					logConstants.ERROR,
					`Failed to load keyboard settings: ${error}`,
					AotCategory.Error,
					`Failed to load keyboard settings: ${error}`
				);
				setIsLoading(false);
			}
		};

		loadSettings();
	}, []);

	const handleInputModeChange = async (value: string) => {
		try {
			// Update local state immediately
			setInputMode(value);

			// Save to native bridge
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE_SELECTION,
				settingValue: value,
			});

			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Keyboard input mode changed to: ${value}`,
				AotCategory.Info,
				`Keyboard input mode changed to: ${value}`
			);
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save keyboard input mode: ${error}`,
				AotCategory.Error,
				`Failed to save keyboard input mode: ${error}`
			);
			// Revert to previous value on error
			const bridge = SettingsNativeBridgeFactory.getBridge();
			const response = await bridge.getSetting({
				settingKey: KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE_SELECTION,
			});
			if (response && response.status === 0 && response.data?.settingValue) {
				setInputMode(response.data.settingValue);
			}
		}
	};

	const handleUnicodeModeSetting = async (settingId: string, value: any) => {
		try {
			// Update local state immediately
			setUnicodeModeSettings((prev) => ({
				...prev,
				[settingId]: { ...prev[settingId], value },
			}));

			// Save to native bridge
			const bridge = SettingsNativeBridgeFactory.getBridge();
			await bridge.setSetting({
				settingKey: settingId,
				settingValue: value,
			});

			SettingsLogger.getInstance().write(
				logConstants.INFO,
				`Unicode mode setting ${settingId} changed to: ${value}`,
				AotCategory.Info,
				`Unicode mode setting ${settingId} changed to: ${value}`
			);
		} catch (error) {
			SettingsLogger.getInstance().write(
				logConstants.ERROR,
				`Failed to save Unicode mode setting ${settingId}: ${error}`,
				AotCategory.Error,
				`Failed to save Unicode mode setting ${settingId}: ${error}`
			);
		}
	};

	// Get the setting configuration
	const config = getSettingConfig(
		KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE_SELECTION
	) as RadioSettingConfig;

	if (isLoading || !config) {
		return (
			<SettingsPage
				title={title}
				canNavigateBack={!!onNavigatingBack}
				onNavigatingBack={onNavigatingBack}
				settings={<div>Loading...</div>}
			/>
		);
	}

	return (
		<SettingsPage
			title={title}
			canNavigateBack={!!onNavigatingBack}
			onNavigatingBack={onNavigatingBack}
			settings={
				<>
					<RadioSetting
						settingId={KEYBOARD_SETTING_IDS.KEYBOARD_INPUT_MODE_SELECTION}
						{...config}
						selectedValue={inputMode}
						onChange={handleInputModeChange}
					/>
					<KeyboardUnicodeModeSetting
						settingsState={unicodeModeSettings}
						handleChange={handleUnicodeModeSetting}
					/>
				</>
			}
		/>
	);
};
